/**
 * Shared Inventory Utilities
 * Common functions used across all inventory types
 */

/**
 * Format quantity based on inventory type configuration
 * @param {number} quantity - The quantity to format
 * @param {object} config - Inventory configuration
 * @returns {string} Formatted quantity string
 */
export const formatQuantity = (quantity, config) => {
  if (!quantity && quantity !== 0) return 'N/A';
  
  if (config.quantityType === 'decimal') {
    return parseFloat(quantity).toFixed(4).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  
  return parseInt(quantity).toLocaleString();
};

/**
 * Format unit breakdown for display (products only)
 * @param {object} item - Inventory item with unit breakdown
 * @returns {string} Formatted unit breakdown string
 */
export const formatUnitBreakdown = (item) => {
  if (!item) return '';
  
  const parts = [];
  
  if (item.pallets > 0) {
    parts.push(`${item.pallets} pallet${item.pallets > 1 ? 's' : ''}`);
  }
  if (item.cases > 0) {
    parts.push(`${item.cases} case${item.cases > 1 ? 's' : ''}`);
  }
  if (item.units > 0) {
    parts.push(`${item.units} unit${item.units > 1 ? 's' : ''}`);
  }
  
  return parts.join(', ') || '0 units';
};

/**
 * Calculate total base units (for products)
 * @param {number} pallets - Number of pallets
 * @param {number} cases - Number of cases
 * @param {number} units - Number of units
 * @param {number} unitsPerCase - Units per case
 * @param {number} casesPerPallet - Cases per pallet
 * @returns {number} Total base units
 */
export const calculateTotalBaseUnits = (pallets, cases, units, unitsPerCase, casesPerPallet) => {
  const palletsToUnits = (pallets || 0) * (casesPerPallet || 0) * (unitsPerCase || 0);
  const casesToUnits = (cases || 0) * (unitsPerCase || 0);
  const looseUnits = units || 0;
  
  return palletsToUnits + casesToUnits + looseUnits;
};

/**
 * Get status tag based on quantity and reorder point
 * @param {number} currentQuantity - Current inventory quantity
 * @param {number} reorderPoint - Reorder point threshold
 * @returns {object} Status object with label and color
 */
export const getInventoryStatus = (currentQuantity, reorderPoint) => {
  if (currentQuantity === 0 || !currentQuantity) {
    return { label: 'Out of Stock', color: 'red' };
  }
  
  if (reorderPoint && currentQuantity <= reorderPoint) {
    return { label: 'Low Stock', color: 'yellow' };
  }
  
  return { label: 'In Stock', color: 'green' };
};

/**
 * Check if lot/batch is expired
 * @param {string} expiryDate - Expiry date string
 * @returns {boolean} True if expired
 */
export const isExpired = (expiryDate) => {
  if (!expiryDate) return false;
  return new Date(expiryDate) < new Date();
};

/**
 * Get expiry status with warning periods
 * @param {string} expiryDate - Expiry date string
 * @returns {object} Expiry status object with label and color
 */
export const getExpiryStatus = (expiryDate) => {
  if (!expiryDate) {
    return { label: 'No Expiry', color: 'gray' };
  }
  
  const expiry = new Date(expiryDate);
  const today = new Date();
  const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
  
  if (daysUntilExpiry < 0) {
    return { label: 'Expired', color: 'red' };
  }
  
  if (daysUntilExpiry <= 30) {
    return { label: `Expires in ${daysUntilExpiry} days`, color: 'orange' };
  }
  
  if (daysUntilExpiry <= 90) {
    return { label: `Expires in ${daysUntilExpiry} days`, color: 'yellow' };
  }
  
  return { label: 'Active', color: 'green' };
};

/**
 * Format date for display
 * @param {string} dateString - Date string
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';

  // Parse date-only strings (YYYY-MM-DD) as local time to avoid UTC midnight shifting the day back
  const date = /^\d{4}-\d{2}-\d{2}$/.test(dateString)
    ? new Date(dateString + 'T00:00:00')
    : new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format currency
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return 'N/A';
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

/**
 * Format vendor and invoice information
 * @param {object} transaction - Transaction object with vendor/invoice data
 * @returns {string} Formatted vendor/invoice info
 */
export const formatVendorInvoice = (transaction) => {
  if (!transaction) return 'N/A';
  
  const parts = [];
  
  if (transaction.supplier_name) {
    parts.push(`Vendor: ${transaction.supplier_name}`);
  }
  
  if (transaction.po_number) {
    const poDate = transaction.po_date ? ` (${formatDate(transaction.po_date)})` : '';
    parts.push(`PO: ${transaction.po_number}${poDate}`);
  }
  
  if (transaction.invoice_number) {
    const invDate = transaction.invoice_date ? ` (${formatDate(transaction.invoice_date)})` : '';
    parts.push(`Invoice: ${transaction.invoice_number}${invDate}`);
  }
  
  if (transaction.unit_cost) {
    parts.push(`Cost: ${formatCurrency(transaction.unit_cost)}`);
  }
  
  return parts.length > 0 ? parts.join('\n') : 'N/A';
};

/**
 * Group inventory items by location
 * @param {array} items - Array of inventory items
 * @returns {object} Items grouped by location
 */
export const groupByLocation = (items) => {
  return items.reduce((acc, item) => {
    const locationId = item.location_id || 'unknown';
    if (!acc[locationId]) {
      acc[locationId] = [];
    }
    acc[locationId].push(item);
    return acc;
  }, {});
};

/**
 * Calculate aggregate inventory (sum quantities by item and location)
 * @param {array} items - Array of inventory items
 * @returns {array} Aggregated inventory
 */
export const calculateAggregateInventory = (items) => {
  const aggregated = {};
  
  items.forEach(item => {
    const key = `${item.item_id}_${item.location_id}`;
    
    if (!aggregated[key]) {
      aggregated[key] = {
        ...item,
        total_quantity: 0,
        lot_count: 0,
        lots: []
      };
    }
    
    aggregated[key].total_quantity += item.quantity || 0;
    aggregated[key].lot_count += 1;
    aggregated[key].lots.push(item);
  });
  
  return Object.values(aggregated);
};

/**
 * Filter inventory items
 * @param {array} items - Array of inventory items
 * @param {object} filters - Filter criteria
 * @returns {array} Filtered items
 */
export const filterInventoryItems = (items, filters) => {
  return items.filter(item => {
    // Filter by search term
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const matchesSearch = 
        (item.name && item.name.toLowerCase().includes(searchLower)) ||
        (item.sku && item.sku.toLowerCase().includes(searchLower)) ||
        (item.lot_number && item.lot_number.toLowerCase().includes(searchLower)) ||
        (item.batch_number && item.batch_number.toLowerCase().includes(searchLower));
      
      if (!matchesSearch) return false;
    }
    
    // Filter by location
    if (filters.location && filters.location !== 'all') {
      if (item.location_id !== filters.location) return false;
    }
    
    // Filter by status
    if (filters.status && filters.status !== 'all') {
      const status = getInventoryStatus(item.quantity, item.reorder_point);
      if (status.label.toLowerCase() !== filters.status.toLowerCase()) return false;
    }
    
    return true;
  });
};

/**
 * Sort inventory items
 * @param {array} items - Array of inventory items
 * @param {string} sortBy - Field to sort by
 * @param {string} sortOrder - 'asc' or 'desc'
 * @returns {array} Sorted items
 */
export const sortInventoryItems = (items, sortBy, sortOrder = 'asc') => {
  return [...items].sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];
    
    // Handle null/undefined values
    if (aVal == null) aVal = '';
    if (bVal == null) bVal = '';
    
    // Compare
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });
};

/**
 * Check if documents exist for an item
 * @param {object} item - Inventory item
 * @returns {object} Document availability
 */
export const checkDocumentAvailability = (item) => {
  return {
    invoice: !!item.invoice_file_path,
    po: !!item.po_file_path,
    coa: !!item.coa_file_path,
    msds: !!item.msds_file_path,
    bol: !!item.bol_file_path,
    sds: !!item.sds_file_path,
    hasAny: !!(
      item.invoice_file_path || 
      item.po_file_path || 
      item.coa_file_path || 
      item.msds_file_path || 
      item.bol_file_path ||
      item.sds_file_path
    )
  };
};

/**
 * Generate lot/batch label
 * @param {object} item - Inventory item
 * @returns {string} Lot/batch label
 */
export const generateLotBatchLabel = (item) => {
  const parts = [];
  
  if (item.lot_number) {
    parts.push(`Lot: ${item.lot_number}`);
  }
  
  if (item.batch_number) {
    parts.push(`Batch: ${item.batch_number}`);
  }
  
  return parts.length > 0 ? parts.join(' | ') : 'No Lot/Batch';
};

/**
 * Validate quantity input based on inventory type
 * @param {string} value - Input value
 * @param {object} config - Inventory configuration
 * @returns {object} Validation result
 */
export const validateQuantity = (value, config) => {
  if (!value || value === '') {
    return { isValid: false, error: 'Quantity is required' };
  }
  
  const num = parseFloat(value);
  
  if (isNaN(num)) {
    return { isValid: false, error: 'Invalid quantity' };
  }
  
  if (num <= 0) {
    return { isValid: false, error: 'Quantity must be greater than 0' };
  }
  
  // For integer types, check if it's a whole number
  if (config.quantityType === 'integer' && !Number.isInteger(num)) {
    return { isValid: false, error: 'Quantity must be a whole number' };
  }
  
  return { isValid: true, error: null };
};

/**
 * Calculate inventory value
 * @param {array} items - Array of inventory items
 * @returns {number} Total inventory value
 */
export const calculateInventoryValue = (items) => {
  return items.reduce((total, item) => {
    const quantity = item.quantity || 0;
    const unitCost = item.unit_cost || 0;
    return total + (quantity * unitCost);
  }, 0);
};

/**
 * Get low stock items
 * @param {array} items - Array of inventory items
 * @returns {array} Items that are low stock or out of stock
 */
export const getLowStockItems = (items) => {
  return items.filter(item => {
    const status = getInventoryStatus(item.quantity, item.reorder_point);
    return status.label === 'Low Stock' || status.label === 'Out of Stock';
  });
};

/**
 * Get expired items
 * @param {array} items - Array of inventory items
 * @returns {array} Items that are expired or expiring soon
 */
export const getExpiringItems = (items) => {
  return items.filter(item => {
    if (!item.expiry_date) return false;
    const status = getExpiryStatus(item.expiry_date);
    return status.label === 'Expired' || status.color === 'orange' || status.color === 'yellow';
  });
};

export default {
  formatQuantity,
  formatUnitBreakdown,
  calculateTotalBaseUnits,
  getInventoryStatus,
  isExpired,
  getExpiryStatus,
  formatDate,
  formatCurrency,
  formatVendorInvoice,
  groupByLocation,
  calculateAggregateInventory,
  filterInventoryItems,
  sortInventoryItems,
  checkDocumentAvailability,
  generateLotBatchLabel,
  validateQuantity,
  calculateInventoryValue,
  getLowStockItems,
  getExpiringItems
};
